import {useState} from 'react'
import {View} from 'react-native'
import {Trans, useLingui} from '@lingui/react/macro'

import {
  OnboardingControls,
  OnboardingDescriptionText,
  OnboardingPosition,
  OnboardingTitleText,
} from '#/screens/Onboarding/Layout'
import {useOnboardingInternalState} from '#/screens/Onboarding/state'
import {CREATOR_ROLES} from '#/screens/Onboarding/StepGoal/data'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Toggle from '#/components/forms/Toggle'
import {Text} from '#/components/Typography'

export function StepCreatorRole() {
  const {i18n, t: l} = useLingui()
  const t = useTheme()
  const {state, dispatch} = useOnboardingInternalState()

  const [roleId, setRoleId] = useState<string | null>(
    state.goalStepResults.creatorRole,
  )

  const selectedRole = CREATOR_ROLES.find(r => r.id === roleId) ?? null

  function onContinue() {
    if (!roleId) return
    dispatch({type: 'setGoalResults', results: {creatorRole: roleId}})
    dispatch({type: 'next'})
  }

  return (
    <View style={[a.align_start, a.gap_sm]} testID="onboardingCreatorRole">
      <OnboardingPosition />
      <OnboardingTitleText>
        <Trans>Define Your Creator Identity 🎭</Trans>
      </OnboardingTitleText>
      <OnboardingDescriptionText>
        <Trans>
          Your role shapes your content, your audience, and your impact — pick
          the one that fits how you show up.
        </Trans>
      </OnboardingDescriptionText>

      <View style={[a.w_full, a.pt_lg]}>
        <Toggle.Group
          values={roleId ? [roleId] : []}
          onChange={vals => setRoleId(vals[vals.length - 1] ?? null)}
          label={l`Select your creator role`}>
          <View style={[a.flex_row, a.flex_wrap, a.gap_sm]}>
            {CREATOR_ROLES.map(role => (
              <Toggle.Item
                key={role.id}
                name={role.id}
                label={i18n._(role.label)}>
                {({selected}) => (
                  <View
                    style={[
                      a.px_md,
                      a.py_sm,
                      a.rounded_full,
                      a.border,
                      selected
                        ? {
                            borderColor: 'transparent',
                            ...t.atoms.bg_contrast_975,
                          }
                        : t.atoms.border_contrast_low,
                    ]}>
                    <Text
                      style={[
                        a.text_sm,
                        a.font_bold,
                        selected && t.atoms.text_inverted,
                      ]}
                      emoji>
                      {role.icon} {i18n._(role.label)}
                    </Text>
                  </View>
                )}
              </Toggle.Item>
            ))}
          </View>
        </Toggle.Group>
      </View>

      {selectedRole && (
        <View
          style={[
            a.w_full,
            a.mt_md,
            a.p_md,
            a.border,
            {
              borderColor: t.atoms.border_contrast_low.borderColor,
              backgroundColor: t.atoms.bg_contrast_25.backgroundColor,
              borderRadius: 18,
            },
          ]}>
          <Text style={[a.text_sm, a.font_bold]} emoji>
            {selectedRole.icon} {i18n._(selectedRole.label)}
          </Text>
          <Text style={[a.text_sm, a.mt_xs, t.atoms.text_contrast_medium]}>
            {i18n._(selectedRole.desc)}
          </Text>
        </View>
      )}

      <OnboardingControls.Portal>
        <Button
          disabled={!roleId}
          testID="onboardingContinue"
          color="primary"
          size="large"
          label={l`Continue to next step`}
          onPress={onContinue}>
          <ButtonText>
            <Trans>Continue</Trans>
          </ButtonText>
        </Button>
      </OnboardingControls.Portal>
    </View>
  )
}
